import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  Tabs,
  Tab,
  Typography,
  Divider,
  Tooltip,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import SaveIcon from "@mui/icons-material/Save";
import RedoIcon from "@mui/icons-material/Redo";
import UndoIcon from "@mui/icons-material/Undo";
import Clear from "@mui/icons-material/Clear";
import ListAltIcon from "@mui/icons-material/ListAlt";
import MapIcon from "@mui/icons-material/Map";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DragHandleIcon from "@mui/icons-material/DragHandle";

import { useNavigate, useParams } from "react-router-dom";
import CreatePropertyDialog from "../property/CreatePropertyForm";
import { useGetAllRooms } from "../../hooks/query/useRoom";
import FallbackLoader from "../loading/FallBackLoader";

import { useCheckVastuRemedy } from "../../hooks/query/useVastu";
import { toast } from "react-toastify";
import { DirectionRequest, VastuRequest } from "../../types/VastuRemedy";
import ActivityTabActivity from "../activitytabs/ActivtyTabActivity";
import ActivityTabSitePlan from "../activitytabs/ActivityTabSitePlan";
import { DroppedItem, Point } from "../../pages/Home/types";
import { calculatDistance } from "../../pages/Home/helpers";

// Tiny helper type-guard for pointer events
function isPointerEvent(e: Event): e is PointerEvent {
  return (e as PointerEvent).pointerType !== undefined;
}

interface ActivityBarProps {
  isOpen: boolean;
  toggleMenu: () => void;
  onCompassOpacityChange: (value: number) => void;
  onItemDrop: (
    itemId: string,
    itemName: string,
    itemType: string,
    x: number,
    y: number
  ) => void;
  toggleShowCompass: () => void;
  toggleShowMarma: () => void;
  confirmClearMap: () => void;
  handleSaveMapState?: () => void;
  showCompass: boolean;
  handleUndo?: () => void;
  handleRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  showMarma?: boolean;
  isPinning?: boolean;
  droppedItems?: DroppedItem[];
  directions: any[] | undefined;
  isMobile?: boolean;
  isTablet?: boolean;
  sitePlanImage?: string | null;
  setSitePlanImage?: React.Dispatch<React.SetStateAction<string | null>>;
  sitePlanPoints?: { x: number; y: number }[];
  setSitePlanPoints?: React.Dispatch<
    React.SetStateAction<{ x: number; y: number }[]>
  >;
  marmaPoints: Point[];

  // map matching
  mapPoints?: { lat: number; lng: number }[];
  setMapPoints?: React.Dispatch<
    React.SetStateAction<{ lat: number; lng: number }[]>
  >;
  isMatchingSitePlanPoints?: boolean;
  setIsMatchingSitePlanPoints?: React.Dispatch<React.SetStateAction<boolean>>;
}

const ActivityBar: React.FC<ActivityBarProps> = ({
  isOpen,
  toggleMenu,
  onItemDrop,
  onCompassOpacityChange,
  showCompass,
  handleSaveMapState,
  toggleShowCompass,
  handleUndo,
  handleRedo,
  canUndo = false,
  canRedo = false,
  isPinning,
  droppedItems,
  directions,
  isMobile,
  confirmClearMap,
  isTablet,
  showMarma,
  toggleShowMarma,
  sitePlanImage,
  setSitePlanImage,
  sitePlanPoints,
  setSitePlanPoints,
  isMatchingSitePlanPoints,
  setIsMatchingSitePlanPoints,
  marmaPoints,
}) => {
  const navigate = useNavigate();
  const { propertyId } = useParams();

  const [activeTab, setActiveTab] = useState<number>(0);
  const [compassOpacity, setCompassOpacity] = useState<number>(1);
  const [openCreateDialog, setOpenCreateDialog] = React.useState(false);

  // Keep barHeight in state so the UI updates (used on mobile for vertical resizing).
  const [barHeight, setBarHeight] = useState<number>(40);

  // Refs to store data used during the drag
  const isDraggingRef = useRef(false);
  const dragStartYRef = useRef(0);
  const dragStartHeightRef = useRef(barHeight);
  // Keep a ref in sync with barHeight, so pointermove logic always sees the latest
  const barHeightRef = useRef(barHeight);
  useEffect(() => {
    barHeightRef.current = barHeight;
  }, [barHeight]);

  // The drag handle ref (the part you actually click/touch on to resize)
  const dragHandleRef = useRef<HTMLDivElement>(null);

  // For the scrollable content area
  const contentRef = useRef<HTMLDivElement>(null);

  // --- Touch-based SCROLLING in the content area (on mobile) ---
  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement || !isMobile) return;

    let startY = 0;
    let startScrollTop = 0;
    let touchInProgress = false;

    const handleTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
      startScrollTop = contentElement.scrollTop;
      touchInProgress = true;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!touchInProgress) return;
      const deltaY = startY - e.touches[0].clientY;
      contentElement.scrollTop = startScrollTop + deltaY;

      // Prevent default only if we're scrolling within bounds
      if (
        (deltaY > 0 &&
          contentElement.scrollTop <
            contentElement.scrollHeight - contentElement.clientHeight) ||
        (deltaY < 0 && contentElement.scrollTop > 0)
      ) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = () => {
      touchInProgress = false;
    };

    contentElement.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    contentElement.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    contentElement.addEventListener("touchend", handleTouchEnd);

    return () => {
      contentElement.removeEventListener("touchstart", handleTouchStart);
      contentElement.removeEventListener("touchmove", handleTouchMove);
      contentElement.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isMobile, isOpen]);

  useEffect(() => {
    const onPointerDown = (e: Event) => {
      if (!isPointerEvent(e)) return;
      if (!dragHandleRef.current) return;

      const handleEl = dragHandleRef.current;
      if (!handleEl.contains(e.target as Node)) return;

      if (!isMobile) return;

      e.preventDefault();
      e.stopPropagation();
      isDraggingRef.current = true;
      dragStartYRef.current = e.clientY;
      dragStartHeightRef.current = barHeightRef.current;
    };

    const onPointerMove = (e: Event) => {
      if (!isPointerEvent(e)) return;
      if (!isDraggingRef.current) return;

      e.preventDefault();
      e.stopPropagation();

      const deltaY = dragStartYRef.current - e.clientY;
      const newHeight = Math.max(
        25,
        Math.min(75, dragStartHeightRef.current + deltaY * 0.25)
      );
      setBarHeight(newHeight);
    };

    const onPointerUp = (e: Event) => {
      if (!isPointerEvent(e)) return;
      if (!isDraggingRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      isDraggingRef.current = false;
    };

    document.addEventListener("pointerdown", onPointerDown, { passive: false });
    document.addEventListener("pointermove", onPointerMove, { passive: false });
    document.addEventListener("pointerup", onPointerUp, { passive: false });

    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
    };
  }, [isMobile]);

  const handleTabChange = (_: React.SyntheticEvent, newTab: number) => {
    setActiveTab(newTab);
  };

  const handlePrevTab = () =>
    setActiveTab((prev) => (prev === 0 ? 1 : prev - 1));
  const handleNextTab = () =>
    setActiveTab((prev) => (prev === 1 ? 0 : prev + 1));

  const handleOpenCreateDialog = () => setOpenCreateDialog(true);
  const handleCloseCreateDialog = () => setOpenCreateDialog(false);

  const handleOpacityChange = (value: number | number[]) => {
    const newOpacity = Array.isArray(value) ? value[0] : value;
    setCompassOpacity(newOpacity);
    onCompassOpacityChange(newOpacity);
  };

  const { mutateAsync: checkVastuRemedy } = useCheckVastuRemedy();

  const handleSubmit = async () => {
    if (!directions || directions.length === 0) {
      console.log("No directions found.");
      return;
    }

    const finalData: VastuRequest = directions
      .map((dir) => {
        const itemsInDirection = droppedItems?.filter((item) =>
          item.directions?.includes(dir.id)
        );
        if (!itemsInDirection || itemsInDirection.length === 0) return null;

        const directionObj: DirectionRequest = {
          direction: dir.id,
        };

        for (const item of itemsInDirection) {
          const cleanId = item.id?.split("+")[0];
          if (item.type === "entrances" && item.entranceSubzones) {
            directionObj.entrances = directionObj.entrances || [];
            directionObj.entrances.push(item.entranceSubzones);
          } else if (item.type === "housePillar" || item.type === "wall") {
            const itemX = item.x ?? item.lat;
            const itemY = item.y ?? item.lng;

            const nearMarma = marmaPoints.some((marma) => {
              const marmaX = marma.x ?? marma.lat;
              const marmaY = marma.y ?? marma.lng;
              const hasLatLng =
                typeof item.lat === "number" &&
                typeof item.lng === "number" &&
                typeof marma.lat === "number" &&
                typeof marma.lng === "number";

              const threshold = hasLatLng ? 3 : 10;
              return (
                calculatDistance(
                  itemX,
                  itemY,
                  marmaX,
                  marmaY,
                  item.lat,
                  item.lng,
                  marma.lat,
                  marma.lng
                ) < threshold
              );
            });

            if (nearMarma) {
              directionObj.marma = directionObj.marma || [];
              directionObj.marma.push(cleanId!);
            }
          } else if (
            item.type === "toiletSeat" ||
            item.type === "kitchenBurnerSlab"
          ) {
            directionObj[item.type] = directionObj[item.type] || [];
            if (item.color) {
              directionObj[item.type]!.push(item.color);
            }
          } else if (item.type === "Room") {
            directionObj.rooms = directionObj.rooms || [];
            if (cleanId) {
              directionObj.rooms.push(cleanId);
            }
          }
        }

        Object.keys(directionObj).forEach((key) => {
          const val = directionObj[key as keyof DirectionRequest];
          if (Array.isArray(val) && val.length === 0) {
            delete directionObj[key as keyof DirectionRequest];
          }
        });

        if (Object.keys(directionObj).length === 1) return null;
        return directionObj;
      })
      .filter(Boolean) as DirectionRequest[];

    console.log("Final Data for remedy check:", finalData);

    await checkVastuRemedy(
      { requestBody: finalData, propertyId },
      {
        onSuccess: (response) => {
          toast.success("Vastu remedy check completed successfully");
          navigate(`/remedy/${response?.data?.id}`);
          if (handleSaveMapState) handleSaveMapState();
        },
        onError: (error: any) => {
          toast.error("Failed to check Vastu remedy");
          console.error("Error:", error);
        },
      }
    );
  };

  const { data: roomData, isLoading: isRoomLoading } = useGetAllRooms();
  if (isRoomLoading) {
    return <FallbackLoader />;
  }

  const actionBarHeight = isMobile ? 56 : 64;
  const headerHeight = isMobile ? 52 : 56;
  const dragHandleHeight = isMobile ? 24 : 0;

  return (
    <Box
      sx={{
        position: "absolute",
        top: isMobile ? `${100 - barHeight}%` : "8%",
        right: isMobile ? 0 : 20,
        height: isMobile ? `${barHeight}%` : "70%",
        width: isOpen ? (isMobile ? "100%" : isTablet ? 280 : 320) : 0,
        backgroundColor: "#363636",
        borderRadius: isMobile ? "16px 16px 0 0" : "1rem",
        color: "white",
        overflow: "hidden",
        transition: isMobile
          ? "top 0.3s ease, height 0.3s ease"
          : "width 0.3s ease",
        zIndex: 1100,
        display: "flex",
        flexDirection: "column",
        boxShadow: isOpen
          ? isMobile
            ? "0px -4px 12px rgba(0, 0, 0, 0.6)"
            : "-4px 0px 12px rgba(0, 0, 0, 0.6)"
          : "none",
      }}
    >
      {isOpen && isMobile && (
        <Box
          ref={dragHandleRef}
          sx={{
            height: dragHandleHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "ns-resize",
            backgroundColor: "#444",
            borderTopLeftRadius: "16px",
            borderTopRightRadius: "16px",
            borderBottom: "1px solid #555",
            touchAction: "none",
            WebkitTouchCallout: "none",
            WebkitUserSelect: "none",
            userSelect: "none",
          }}
        >
          <DragHandleIcon fontSize="small" sx={{ color: "#888" }} />
        </Box>
      )}

      {isOpen && (
        <>
          <Box
            sx={{
              height: headerHeight,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #444",
              padding: "8px 12px",
              background: "linear-gradient(to bottom, #404040, #363636)",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              textColor="inherit"
              indicatorColor="primary"
              variant="scrollable"
              scrollButtons={isMobile ? false : "auto"}
              sx={{
                minHeight: isMobile ? "36px" : "40px",
                "& .MuiTab-root": {
                  minWidth: isMobile ? "64px" : "72px",
                  textTransform: "none",
                },
              }}
            >
              <Tab
                icon={
                  <Tooltip title="Activity" arrow>
                    <ListAltIcon />
                  </Tooltip>
                }
                sx={{ minHeight: "40px", padding: "0 12px" }}
              />
              <Tab
                icon={
                  <Tooltip title="Site Plan" arrow>
                    <MapIcon />
                  </Tooltip>
                }
                sx={{ minHeight: "40px", padding: "0 12px" }}
              />
            </Tabs>

            <Tooltip title="Close" arrow>
              <IconButton
                onClick={toggleMenu}
                sx={{
                  color: "white",
                  padding: isMobile ? "4px" : "6px",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <CloseIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Tooltip>
          </Box>

          <Box
            ref={contentRef}
            sx={{
              flex: 1,
              height: `calc(100% - ${
                headerHeight + actionBarHeight + dragHandleHeight
              }px)`,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              scrollbarWidth: "thin",
              touchAction: "pan-y",
              WebkitOverflowScrolling: "touch",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#555",
                borderRadius: "3px",
              },
            }}
          >
            {!propertyId && (
              <Box sx={{ padding: "16px" }}>
                <Typography
                  variant="h6"
                  sx={{ marginBottom: "12px", color: "white" }}
                >
                  No properties selected
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleOpenCreateDialog}
                  sx={{
                    marginBottom: "20px",
                    backgroundColor: "#1976d2",
                    width: "100%",
                  }}
                >
                  Create Property
                </Button>
                <Divider
                  sx={{
                    width: "100%",
                    margin: "16px 0",
                    "&::before, &::after": {
                      borderColor: "#666",
                    },
                  }}
                >
                  OR
                </Divider>
                <Button
                  variant="contained"
                  onClick={() => navigate("/properties")}
                  sx={{
                    marginTop: "4px",
                    backgroundColor: "#1976d2",
                    width: "100%",
                  }}
                >
                  Select Existing Property
                </Button>
              </Box>
            )}

            {isPinning && (
              <Box sx={{ padding: "16px" }}>
                <Typography variant="h6" sx={{ color: "white" }}>
                  Please pin the boundary on the map
                </Typography>
              </Box>
            )}

            {propertyId && !isPinning && (
              <Box sx={{ padding: "12px" }}>
                {activeTab === 0 && (
                  <ActivityTabActivity
                    showCompass={showCompass}
                    toggleShowCompass={toggleShowCompass}
                    showMarma={showMarma || false}
                    toggleShowMarma={toggleShowMarma}
                    compassOpacity={compassOpacity}
                    onCompassOpacityChange={handleOpacityChange}
                    onSitePlanSelected={() => setActiveTab(1)}
                    onGriddedBoxSelected={() => {
                      navigate(`/analysis/${propertyId}`);
                    }}
                    roomData={roomData}
                    isRoomLoading={isRoomLoading}
                    onItemDrop={onItemDrop}
                  />
                )}

                {activeTab === 1 && (
                  <ActivityTabSitePlan
                    sitePlanImage={sitePlanImage}
                    setSitePlanImage={setSitePlanImage}
                    sitePlanPoints={sitePlanPoints}
                    setSitePlanPoints={setSitePlanPoints}
                    isMatchingSitePlanPoints={isMatchingSitePlanPoints}
                    setIsMatchingSitePlanPoints={setIsMatchingSitePlanPoints}
                    propertyId={propertyId}
                  />
                )}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              height: actionBarHeight,
              backgroundColor: "#444",
              borderTop: "1px solid #666",
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
              padding: "0",
              boxShadow: "0px -2px 8px rgba(0,0,0,0.2)",
            }}
          >
            <Tooltip title="Previous Tab" arrow>
              <IconButton
                onClick={handlePrevTab}
                sx={{
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Generate Report" arrow>
              <IconButton
                onClick={handleSubmit}
                sx={{
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Save" arrow>
              <IconButton
                onClick={handleSaveMapState}
                sx={{
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <SaveIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Redo" arrow>
              <span>
                <IconButton
                  onClick={handleRedo}
                  disabled={!canRedo || !isPinning}
                  sx={{
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  <RedoIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Undo" arrow>
              <span>
                <IconButton
                  onClick={handleUndo}
                  disabled={!canUndo || !isPinning}
                  sx={{
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  <UndoIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Clear" arrow>
              <span>
                <IconButton
                  onClick={confirmClearMap}
                  sx={{
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  <Clear />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Next Tab" arrow>
              <IconButton
                onClick={handleNextTab}
                sx={{
                  color: "white",
                  "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <CreatePropertyDialog
            open={openCreateDialog}
            onClose={handleCloseCreateDialog}
          />
        </>
      )}
    </Box>
  );
};

export default ActivityBar;
